<template>
  <v-menu
    ref="menu"
    v-model="menu"
    class="date-picker"
    :close-on-click="!isTyping"
    :close-on-content-click="false"
    min-width="290px"
    offset-y
    transition="scale-transition"
  >
    <template #activator="{ attrs }">
      <div
        class="mf-date-range-picker-input-container"
        :class="{ 'mf-date-range-picker-dynamic': isDynamicPeriod, 'mf-date-range-picker-disabled': disabled }"
        @click="handleClick"
      >
        <mf-icon :color="disabled ? 'cinza-0' : 'marca-0-l40'" fill icon="event" />
        <span>{{ formatedDate.initialDate }}</span>
        <div class="separator" />
        <span>{{ formatedDate.finalDate }}</span>
      </div>
    </template>
    <v-date-picker
      v-model="date"
      data-cy="month-range-picker"
      locale="pt-BR"
      :max="max"
      :min="min"
      range
      :selected-items-text="date[0] && date[1] ? '2 selecionadas' : ''"
      type="month"
    />
  </v-menu>
</template>

<script>
import { DateTime } from 'luxon'
import { mapGetters } from 'vuex'
export default {
  name: 'MonthRangePicker',
  props: {
    initialDate: {
      type: String,
      default: '',
    },
    finalDate: {
      type: String,
      default: '',
    },
    min: {
      type: String,
      default: '',
    },
    max: {
      type: String,
      default: '',
    },
    isDynamicPeriod: {
      type: Boolean,
      default: false,
    },
    variant: {
      type: String,
      default: 'current',
      validator: (value) => ['current', 'reference'].includes(value),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isNotRecurrent: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    menu: false,
    date: [],
    isTyping: false,
  }),
  computed: {
    ...mapGetters(['currentPeriod', 'referencePeriod']),
    formatedDate() {
      if (!this.date || !this.initialDate) {
        const dates = {
          initialDate: '',
          finalDate: '',
        }
        this.$emit('set-dates', dates)
        return dates
      }
      let dates = {}

      if (this.isDynamicPeriod || this.isNotRecurrent) {
        const period = this.variant === 'current' ? this.currentPeriod : this.referencePeriod
        dates = {
          initialDate: DateTime.fromFormat(period.initialDate, 'yyyy-MM-dd').setLocale('pt-BR').toFormat("dd 'de' MMMM yyyy"),
          finalDate: DateTime.fromFormat(period.finalDate, 'yyyy-MM-dd').setLocale('pt-BR').toFormat("dd 'de' MMMM yyyy"),
        }
      } else {
        const initialYear = DateTime.fromFormat(this.date[0].toString(), 'yyyy-MM').toFormat('yyyy')
        const finalYear = DateTime.fromFormat(this.date[1].toString(), 'yyyy-MM').toFormat('yyyy')
        const initialMonthStr = this.getMonth(DateTime.fromFormat(this.date[0].toString(), 'yyyy-MM').toFormat('MM'))
        const finalMonthStr = this.getMonth(DateTime.fromFormat(this.date[1].toString(), 'yyyy-MM').toFormat('MM'))
        dates = {
          initialDate: `${initialMonthStr} ${initialYear}`,
          finalDate: `${finalMonthStr} ${finalYear}`,
        }
      }
      this.$emit('set-dates', dates)
      return dates
    },
    computedMinDate() {
      const today = DateTime.now().toFormat('yyyy-MM-dd')
      if (this.editOnlyEndDate && this.endDateFuture) return DateTime.fromFormat(this.date[0], 'yyyy-MM-dd').plus({ days: 1 }).toFormat('yyyy-MM-dd')
      if (this.endDateFuture && this.date[0] && this.isTyping) return DateTime.fromFormat(this.date[0], 'yyyy-MM-dd').plus({ days: 1 }).toFormat('yyyy-MM-dd')
      if (this.isTyping) return this.date[0]
      if (this.blockPastDate) return today
      return this.min
    },
  },
  watch: {
    date(newVal) {
      if (this.date.length === 1) {
        const endDate = newVal[0]
        const dateObject = DateTime.fromFormat(endDate, 'yyyy-MM')
        const twoMonthsBefore = dateObject.minus({ months: 2 }).toFormat('yyyy-MM')

        this.$emit('update:initialDate', twoMonthsBefore)
        this.$emit('update:finalDate', endDate)
        this.$emit('input', [twoMonthsBefore, endDate])
        this.date = [twoMonthsBefore, endDate]
        this.menu = false
      }
    },
    initialDate: {
      handler(value) {
        this.date[0] = value
      },
      immediate: true,
    },
    finalDate: {
      handler(value) {
        this.date[1] = value
      },
      immediate: true,
    },
  },
  methods: {
    getMonth(month) {
      const months = {
        '01': 'Janeiro',
        '02': 'Fevereiro',
        '03': 'Março',
        '04': 'Abril',
        '05': 'Maio',
        '06': 'Junho',
        '07': 'Julho',
        '08': 'Agosto',
        '09': 'Setembro',
        10: 'Outubro',
        11: 'Novembro',
        12: 'Dezembro',
      }
      return months[month]
    },
    handleClick() {
      if (this.disabled) return
      this.menu = true
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep .v-text-field__details {
  margin: 0 !important;
  padding: 0 !important;
}

#date-picker-icon {
  height: 24px;
}
.v-text-field__details {
  padding-left: 0 !important;
}
.error-message {
  color: #e03210;
}
::v-deep input {
  pointer-events: none !important;
}

.mf-date-range-picker-input-container {
  cursor: pointer;
  max-width: 420px;
  padding: 10px;
  display: flex;
  align-items: center;
  gap: 8px;

  span {
    color: $marca-0-l10;
    font-size: 18px;
    margin: 0;
    font-weight: 400;
    line-height: 1;
  }

  .separator {
    background: $marca-0-l40;
    height: 1px;
    width: 12px;
  }
}

.mf-date-range-picker-dynamic {
  max-width: 460px;
}

.mf-date-range-picker-disabled {
  cursor: not-allowed;
  span {
    color: $cinza-0;
  }
}
</style>
